import { useState } from "react";
import { getClassifyDetail } from "../api/client";
import type { ClassifyDetail } from "../api/client";

/**
 * Shared action envelope for the classify detail panel (R-260 CS-station cleanup).
 *
 * Collapses the six same-shape parked-doc handlers (classify / advance / split /
 * applyOrder / assign / move / merge) that each used to repeat the
 * setBusy/setActionError/try/catch/finally boilerplate around one shared
 * busy + error slot.
 *
 * Two envelopes:
 *  - runTerminal: the action always resolves the doc on success (classify /
 *    accept-partial / split) → calls onResolved.
 *  - runParked: the action returns `{ advanced }` (applyOrder / assign / move /
 *    merge) → onResolved if it advanced, otherwise re-fetch the detail and hand
 *    the fresh copy back via onRefetch.
 */
export function useParkedDocAction(opts: {
  documentId: string;
  onResolved: () => void;
  onRefetch: (fresh: ClassifyDetail) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  async function runTerminal(fn: () => Promise<unknown>, errMsg: string) {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
      opts.onResolved();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : errMsg);
    } finally {
      setBusy(false);
    }
  }

  async function runParked(
    fn: () => Promise<{ advanced?: boolean }>,
    errMsg: string,
    onSuccess?: () => void,
  ) {
    setBusy(true);
    setActionError(null);
    try {
      const res = await fn();
      onSuccess?.();
      if (res.advanced) {
        opts.onResolved();
      } else {
        const fresh = await getClassifyDetail(opts.documentId);
        opts.onRefetch(fresh);
      }
    } catch (e) {
      setActionError(e instanceof Error ? e.message : errMsg);
    } finally {
      setBusy(false);
    }
  }

  return { busy, actionError, setActionError, runTerminal, runParked };
}
